import { useEffect, useState } from "react";
import { X, ExternalLink, ArrowRight, UserPlus, Wallet, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

// First-visit homepage popup — teaches new users the 2 things they always ask:
// how to create a Deriv account (via the partner referral link) and how to add
// money (deposit → TRANSFER to the Options CR… account). Shows once.
const WELCOME_KEY = "vts:home-welcome:v1";

const NewUserWelcomeDialog = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (localStorage.getItem(WELCOME_KEY) === "1") return;
      } catch { /* ignore */ }
      setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(WELCOME_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div data-testid="home-welcome-popup" className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-md rounded-2xl border border-vts-blue/40 bg-vts-surface shadow-2xl shadow-vts-blue/20 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-vts-line bg-vts-blue/10">
          <Rocket className="h-4 w-4 text-vts-blue" />
          <span className="text-sm font-bold text-white font-heading">New here? Start in 3 easy steps</span>
          <button data-testid="home-welcome-close" onClick={dismiss} aria-label="Close"
            className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface text-vts-silver/60 hover:text-white transition">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-3">
            <span className="shrink-0 h-7 w-7 rounded-full bg-vts-blue/20 border border-vts-blue/50 text-vts-blue flex items-center justify-center">
              <UserPlus className="h-3.5 w-3.5" />
            </span>
            <div className="text-[13px] text-vts-silver/85 leading-relaxed">
              <span className="font-semibold text-white">1 · Create your free Deriv account</span> — takes ~2 minutes and
              includes a free <span className="text-white font-semibold">$10,000 demo</span>. Use our official partner link below.
            </div>
          </div>
          <div className="flex gap-3">
            <span className="shrink-0 h-7 w-7 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-300 flex items-center justify-center">
              <Wallet className="h-3.5 w-3.5" />
            </span>
            <div className="text-[13px] text-vts-silver/85 leading-relaxed">
              <span className="font-semibold text-white">2 · Add money the RIGHT way</span> — deposit on Deriv Cashier, then{" "}
              <span className="text-amber-300 font-semibold">transfer it to your Options (CR…) account</span>. That's the account
              VTS trades from — money left in the Wallet is not used.
            </div>
          </div>
          <div className="flex gap-3">
            <span className="shrink-0 h-7 w-7 rounded-full bg-vts-profit/15 border border-vts-profit/50 text-vts-profit flex items-center justify-center">
              <Rocket className="h-3.5 w-3.5" />
            </span>
            <div className="text-[13px] text-vts-silver/85 leading-relaxed">
              <span className="font-semibold text-white">3 · Come back & trade</span> — open the VTS Desk, log in with Deriv, and
              run the bots. There's a full "How to use" guide inside.
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <a data-testid="home-welcome-signup-link" href={DERIV_AFFILIATE_URL} onClick={dismiss}
              className="flex items-center justify-center gap-2 w-full rounded-lg bg-vts-blue hover:bg-vts-blueHover text-white font-bold text-sm py-3 transition">
              Create free Deriv account <ExternalLink className="h-4 w-4" />
            </a>
            <button data-testid="home-welcome-desk-btn" onClick={() => { dismiss(); navigate("/trade?connect=1"); }}
              className="flex items-center justify-center gap-2 w-full rounded-lg border border-vts-line bg-vts-surface hover:bg-vts-surfaceHover text-vts-silver hover:text-white font-semibold text-sm py-3 transition">
              I already have an account — Open VTS Desk <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="text-[10px] text-vts-silver/50 text-center font-mono">
            Signup opens{" "}
            <a data-testid="home-welcome-visible-link" href={DERIV_AFFILIATE_URL} onClick={dismiss}
              className="text-vts-blue underline underline-offset-2">t.deriv.link</a>{" "}
            — our official Deriv partner link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewUserWelcomeDialog;
